import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import TimeTrackLogo from "../components/TimeTrackLogo";
import {
  MapPin, Clock, ShieldCheck, ChartLineUp, Buildings, WhatsappLogo,
  ArrowRight, CheckCircle, Camera, Globe, WifiHigh, Briefcase, Users, ClipboardText
} from "@phosphor-icons/react";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (user) nav("/home"); // logged in? send to app
  }, [user, nav]);

  return (
    <div className="min-h-screen bg-white text-zinc-950" data-testid="landing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-zinc-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 py-3">
          <TimeTrackLogo size={34} variant="lockup" showParent={false} />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#features" className="hover:text-emerald-600" data-testid="nav-features">Features</a>
            <a href="#geography" className="hover:text-emerald-600" data-testid="nav-geography">Geography</a>
            <a href="#pricing" className="hover:text-emerald-600" data-testid="nav-pricing">Pricing</a>
            <a href="https://lomgroupja.com" target="_blank" rel="noreferrer" className="hover:text-emerald-600">LOM family</a>
          </nav>
          <div className="flex gap-2">
            <Link to="/login" className="px-4 py-2 text-sm font-semibold border border-zinc-300 rounded-sm hover:bg-zinc-50" data-testid="nav-signin">Sign in</Link>
            <Link to="/signup" className="px-4 py-2 text-sm font-semibold bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="nav-signup">Start free</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #09090b 1px, transparent 1px), linear-gradient(to bottom, #09090b 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold tracking-wider uppercase">
              <MapPin size={12} weight="fill" />
              GPS-Verified · Made in Jamaica
            </div>
            <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
              Right people.<br/>
              Right place.<br/>
              <span className="text-emerald-600">Right time.</span>
            </h1>
            <p className="text-lg text-zinc-600 max-w-xl leading-relaxed mb-8">
              LOM TimeTrack is a GPS-verified staff attendance & time-management system for teams across multiple locations. Stop timesheet lies. Start knowing exactly who's where.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3.5 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold rounded-sm group" data-testid="hero-signup">
                Start free — 5 staff forever
                <ArrowRight weight="bold" size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3.5 border border-zinc-300 hover:bg-zinc-50 font-semibold rounded-sm" data-testid="hero-demo">
                Try the demo account
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-zinc-500 font-medium">
              <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> No credit card</span>
              <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> 2-minute setup</span>
              <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> Cancel anytime</span>
            </div>
          </div>

          {/* Hero card */}
          <div className="relative">
            <div className="bg-zinc-950 text-white rounded-lg p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[0.65rem] tracking-[0.22em] uppercase text-zinc-400">This morning</div>
                  <div className="font-heading font-black text-3xl tracking-tighter">Downtown Flagship</div>
                </div>
                <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
              </div>
              <div className="space-y-3">
                {[
                  { name: "James Carter", status: "On Time", color: "emerald", time: "07:58" },
                  { name: "Priya Sharma", status: "Late 8m", color: "amber", time: "08:18" },
                  { name: "Sarah Mendez", status: "On Time", color: "emerald", time: "08:02" },
                  { name: "Marcus Reid", status: "Absent", color: "red", time: "—" },
                ].map((r) => (
                  <div key={r.name} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full bg-${r.color}-400`} />
                      <span className="font-medium">{r.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-xs uppercase font-bold tracking-wider text-${r.color}-400`}>{r.status}</span>
                      <span className="font-mono text-sm text-zinc-400">{r.time}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-zinc-800 text-xs text-zinc-400 flex items-center justify-between">
                <span>🔒 GPS verified — 12m accuracy</span>
                <span className="font-mono">08:24 AM</span>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 bg-emerald-500 text-white px-4 py-2 rounded-sm shadow-lg font-heading font-black text-sm rotate-3">
              Live now
            </div>
          </div>
        </div>
      </section>

      {/* Metric strip */}
      <section className="border-y border-zinc-100 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <Metric num="95%" label="On-time rate improvement (avg client)" />
          <Metric num="30 sec" label="Employee clock-in time" />
          <Metric num="±100m" label="GPS accuracy enforced" />
          <Metric num="10mi" label="Geofence radius supported" />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-12">
            <div className="label-eyebrow mb-3">Everything you need</div>
            <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">Built for teams that move around.</h2>
            <p className="text-zinc-600 mt-4">Retail, security, hospitality, deliveries, field service. Any team where "was Marcus at the store at 8am?" is a real question.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Feature icon={MapPin} title="GPS geofencing" desc="Clock-in only allowed within a configurable radius (50m to 10 miles) of each branch." />
            <Feature icon={WifiHigh} title="Store-network verification" desc="Extra security: require the employee to be on the store's Wi-Fi (IP allowlist)." />
            <Feature icon={Briefcase} title="Off-site assignments" desc="Send staff to deliveries, meetings or client visits — they can only clock in at the assigned location." />
            <Feature icon={Camera} title="Photo verification" desc="Optional selfie/photo capture at clock-in for anti-buddy-punching." />
            <Feature icon={Clock} title="Auto classification" desc="Present, On Time, Late, Overtime, Left Early, Absent — all computed in real time using server clock (not spoofable)." />
            <Feature icon={ShieldCheck} title="Audit trail" desc="Every clock-in attempt (including rejected ones) is logged with GPS, IP and reason." />
            <Feature icon={WhatsappLogo} title="WhatsApp alerts" desc="Supervisors get instant WhatsApp alerts on late arrivals, no-shows and out-of-radius attempts." />
            <Feature icon={ChartLineUp} title="Live dashboard" desc="See who's clocked in right now on a live map, plus 7-day trends and per-store breakdowns." />
            <Feature icon={ClipboardText} title="Correction workflow" desc="Employees request fixes for missed clock-outs. Supervisors approve. Every step is audited." />
          </div>
        </div>
      </section>

      {/* Geography section */}
      <section id="geography" className="py-20 md:py-28 bg-zinc-950 text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <div className="max-w-2xl mb-12">
            <div className="label-eyebrow text-emerald-400 mb-3">The differentiator</div>
            <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">Geography is not an afterthought.</h2>
            <p className="text-zinc-400 mt-4">Most attendance apps ask GPS as a checkbox. LOM TimeTrack was built around it.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <FeatureDark title="Click-to-place stores on the map" desc="Admin picks store locations by clicking OpenStreetMap. Radius circle visualized live." />
              <FeatureDark title="Per-store timezone" desc="A store in Kingston and a store in London both flag 'late' correctly." />
              <FeatureDark title="Store network verification" desc="Optional IP allowlist proves the employee is actually on the store Wi-Fi, not just nearby." />
              <FeatureDark title="Off-site assignments override" desc="Supervisor sends staff to Ocho Rios — the clock-in geofence moves with them for that day." />
              <FeatureDark title="Rejected attempts are logged" desc="Every 'nice try' is captured with GPS, distance, IP, device and reason. Supervisor gets a WhatsApp." />
            </div>
            <div className="bg-black rounded-lg p-8 border border-zinc-800">
              <div className="text-xs tracking-[0.22em] uppercase text-zinc-500 mb-3">Recent activity</div>
              <div className="space-y-3 text-sm">
                {[
                  { t: "08:02", who: "James", act: "clocked in", meta: "Downtown · 8m", color: "emerald" },
                  { t: "08:14", who: "Sarah", act: "clocked in", meta: "Airport · 12m · Wi-Fi ✓", color: "emerald" },
                  { t: "08:19", who: "Marcus", act: "rejected", meta: "487m outside radius", color: "red" },
                  { t: "08:21", who: "Priya", act: "clocked in late", meta: "Downtown · 8m late", color: "amber" },
                  { t: "09:45", who: "Ryan", act: "photo submitted", meta: "Off-site: Client visit", color: "emerald" },
                ].map((e, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-zinc-800 last:border-0">
                    <span className="font-mono text-xs text-zinc-500 w-10">{e.t}</span>
                    <span className="font-semibold w-16">{e.who}</span>
                    <span className={`text-xs uppercase font-bold tracking-wider text-${e.color}-400 w-24`}>{e.act}</span>
                    <span className="text-xs text-zinc-500 flex-1">{e.meta}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <div className="label-eyebrow mb-3">Simple pricing</div>
            <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">Priced per active employee. That's it.</h2>
            <p className="text-zinc-600 mt-4">No setup fees. No hidden add-ons. Free forever for small teams.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <PricingCard
              tid="pricing-free"
              name="Starter"
              price="Free"
              per="forever"
              features={["Up to 5 employees", "1 branch", "GPS + geofence", "Live dashboard", "CSV export"]}
              cta="Start free"
              tone="light"
            />
            <PricingCard
              tid="pricing-team"
              name="Team"
              price="$3"
              per="per employee / month"
              features={["Unlimited employees", "Unlimited branches", "Off-site assignments", "WhatsApp notifications", "Excel + PDF reports", "Audit trail"]}
              cta="Book a demo"
              tone="dark"
              highlight
            />
            <PricingCard
              tid="pricing-enterprise"
              name="Enterprise"
              price="Custom"
              per="Volume pricing"
              features={["Everything in Team", "White-label branding", "SSO / SAML", "API access", "Dedicated support", "Custom SLAs"]}
              cta="Contact sales"
              tone="light"
            />
          </div>
          <div className="text-center mt-6 text-xs text-zinc-500">
            Jamaica-priced billing (JMD) available on request.
          </div>
        </div>
      </section>

      {/* CTA - Free trial email capture */}
      <TrialCta />

      {/* Footer */}
      <footer className="bg-zinc-950 text-zinc-300 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-4 gap-8">
          <div>
            <TimeTrackLogo size={36} variant="lockup" invert />
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              GPS-verified staff attendance & time management. Made in Jamaica.
            </p>
          </div>
          <div>
            <div className="label-eyebrow text-zinc-500 mb-3">LOM family</div>
            <ul className="space-y-2 text-sm">
              <li><a href="https://lomgroupja.com" target="_blank" rel="noreferrer" className="hover:text-emerald-400">LOM Solutions</a></li>
              <li><a href="https://lomgroupja.com/jaminventra" target="_blank" rel="noreferrer" className="hover:text-emerald-400">JamInventra</a></li>
              <li><span className="text-zinc-600">LOM Procure <em className="text-[0.65rem]">(coming)</em></span></li>
              <li><span className="text-zinc-600">LOM Analytics <em className="text-[0.65rem]">(coming)</em></span></li>
            </ul>
          </div>
          <div>
            <div className="label-eyebrow text-zinc-500 mb-3">Product</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#features" className="hover:text-emerald-400">Features</a></li>
              <li><a href="#geography" className="hover:text-emerald-400">Geography</a></li>
              <li><a href="#pricing" className="hover:text-emerald-400">Pricing</a></li>
              <li><Link to="/login" className="hover:text-emerald-400">Sign in</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-eyebrow text-zinc-500 mb-3">Company</div>
            <ul className="space-y-2 text-sm">
              <li>LOM Solutions Jamaica Ltd.</li>
              <li><a href="https://lomgroupja.com" target="_blank" rel="noreferrer" className="hover:text-emerald-400">lomgroupja.com</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8 pt-6 border-t border-zinc-800 flex flex-wrap justify-between items-center text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} LOM Solutions Jamaica Ltd. All rights reserved.</div>
          <div className="flex gap-4">
            <a href="#" className="hover:text-emerald-400">Privacy</a>
            <a href="#" className="hover:text-emerald-400">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Metric({ num, label }) {
  return (
    <div>
      <div className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-zinc-950">{num}</div>
      <div className="text-xs text-zinc-500 mt-1 tracking-wider uppercase">{label}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="p-6 border border-zinc-200 rounded-sm hover:border-zinc-950 transition-colors">
      <div className="w-10 h-10 bg-zinc-950 grid place-items-center rounded-sm mb-4">
        <Icon size={18} weight="bold" className="text-white" />
      </div>
      <div className="font-heading font-bold text-lg text-zinc-950 mb-2">{title}</div>
      <div className="text-sm text-zinc-600 leading-relaxed">{desc}</div>
    </div>
  );
}

function FeatureDark({ title, desc }) {
  return (
    <div className="border-l-2 border-emerald-500 pl-4 py-2">
      <div className="font-heading font-bold text-lg text-white mb-1">{title}</div>
      <div className="text-sm text-zinc-400 leading-relaxed">{desc}</div>
    </div>
  );
}

function PricingCard({ name, price, per, features, cta, tone, highlight, tid }) {
  const dark = tone === "dark";
  return (
    <div className={`relative p-8 rounded-sm border transition-all ${
      dark ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-950 border-zinc-200"
    } ${highlight ? "md:-mt-6 md:pb-14 shadow-xl" : ""}`} data-testid={tid}>
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-sm">
          Most popular
        </div>
      )}
      <div className={`label-eyebrow ${dark ? "text-emerald-400" : ""}`}>{name}</div>
      <div className={`mt-4 mb-1 ${dark ? "text-white" : "text-zinc-950"}`}>
        <span className="font-heading font-black text-5xl tracking-tighter">{price}</span>
      </div>
      <div className={`text-sm ${dark ? "text-zinc-400" : "text-zinc-500"} mb-6`}>{per}</div>
      <ul className="space-y-3 mb-8">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <CheckCircle size={16} weight="fill" className={dark ? "text-emerald-400" : "text-emerald-600"} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/signup"
        className={`block text-center px-4 py-3 rounded-sm text-sm font-semibold ${
          dark ? "bg-emerald-500 hover:bg-emerald-400 text-zinc-950" : "bg-zinc-950 hover:bg-zinc-800 text-white"
        }`}
      >
        {cta}
      </Link>
    </div>
  );
}


function TrialCta() {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");
  const [duplicate, setDuplicate] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    setMessage("");
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          company_name: company || null,
          team_size: teamSize || null,
          source: "landing_trial_cta",
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setState("error");
        setMessage(data.detail || "Something went wrong. Please try again.");
        return;
      }
      setDuplicate(!!data.duplicate);
      setMessage(data.message || "Thanks!");
      setState("success");
    } catch (err) {
      setState("error");
      setMessage("Network error. Please try again.");
    }
  };

  const finishSignup = () => {
    // Deep-link into signup with email pre-filled
    const q = new URLSearchParams({ email, company_name: company }).toString();
    nav(`/signup?${q}`);
  };

  return (
    <section className="py-20 md:py-28 bg-emerald-50 border-y border-emerald-200" id="try-free">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-white border border-emerald-300 text-emerald-800 text-xs font-bold tracking-wider uppercase">
            <CheckCircle size={12} weight="fill" />
            14-day free trial · No credit card
          </div>
          <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-3">
            Try LOM TimeTrack <span className="text-emerald-600">free for 14 days.</span>
          </h2>
          <p className="text-zinc-700 text-lg max-w-2xl mx-auto">
            Full access — unlimited stores, GPS geofencing, WhatsApp alerts. Enter your email and we&apos;ll get you set up in minutes.
          </p>
        </div>

        {state === "success" ? (
          <div
            data-testid="trial-cta-success"
            className="max-w-2xl mx-auto bg-white border border-emerald-300 rounded-sm p-8 text-center shadow-lg"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-500 grid place-items-center mx-auto mb-4">
              <CheckCircle size={24} weight="fill" className="text-white" />
            </div>
            <div className="font-heading font-black text-2xl tracking-tight text-zinc-950 mb-2">
              {duplicate ? "You're on the list." : "Trial reserved."}
            </div>
            <p className="text-zinc-600 mb-6">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={finishSignup}
                data-testid="trial-finish-signup-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold rounded-sm"
              >
                Finish signup now <ArrowRight weight="bold" size={14} />
              </button>
              <a
                href="mailto:hello@lomgroupja.com?subject=LOM%20TimeTrack%20demo"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-zinc-300 hover:bg-zinc-50 text-zinc-950 font-semibold rounded-sm"
              >
                Or book a demo call
              </a>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="trial-cta-form"
            className="max-w-2xl mx-auto bg-white border border-zinc-200 rounded-sm p-6 md:p-8 shadow-lg"
          >
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="md:col-span-2">
                <label className="block label-eyebrow mb-1.5">Work email *</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="trial-email-input"
                  placeholder="you@yourcompany.com"
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950"
                />
              </div>
              <div>
                <label className="block label-eyebrow mb-1.5">Company</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  data-testid="trial-company-input"
                  placeholder="Acme Retail"
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950"
                />
              </div>
              <div>
                <label className="block label-eyebrow mb-1.5">Team size</label>
                <select
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  data-testid="trial-team-size-select"
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm bg-white focus:outline-none focus:border-zinc-950"
                >
                  <option value="">Select…</option>
                  <option value="1-10">1–10</option>
                  <option value="11-50">11–50</option>
                  <option value="51-200">51–200</option>
                  <option value="201+">201+</option>
                </select>
              </div>
            </div>

            {state === "error" && (
              <div
                data-testid="trial-cta-error"
                className="mb-4 px-3 py-2 rounded-sm bg-red-50 border border-red-200 text-red-800 text-sm"
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={state === "loading"}
              data-testid="trial-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-zinc-950 hover:bg-zinc-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-sm group"
            >
              {state === "loading" ? "Reserving your trial…" : "Start 14-day free trial"}
              {state !== "loading" && (
                <ArrowRight weight="bold" size={16} className="group-hover:translate-x-0.5 transition-transform" />
              )}
            </button>

            <p className="text-center text-xs text-zinc-500 mt-4">
              No credit card required. Already have an account?{" "}
              <Link to="/login" className="font-semibold text-zinc-950 hover:underline">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
